import * as Sentry from '@sentry/nextjs';
import i18next from 'i18next';

class BaseHttpService {
  private readonly apiUrl: string;

  constructor(apiUrl: string) {
    this.apiUrl = apiUrl;
  }

  public httpGet = async (
    url: string,
    options = {},
    queryParams = {},
    bearerToken: string | null = null
  ) => {
    const queryString = this._buildQueryString(queryParams);
    try {
      return fetch(this._buildAPIUrl(url) + queryString, {
        ...options,
        headers: {
          ...(options['headers'] || {}),
          'Accept-Language': i18next.language || navigator.language || 'en',
          ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {})
        }
      });
    } catch (e) {
      Sentry.captureException(e);
      throw e;
    }
  };

  public httpPost = async (
    url: string,
    options = {},
    body?: any,
    bearerToken: string | null = null
  ) => {
    try {
      return fetch(this._buildAPIUrl(url), {
        ...options,
        method: 'POST',
        headers: {
          ...(options['headers'] || {}),
          'Content-Type': 'application/json',
          ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {})
        },
        body: body ? JSON.stringify(body) : null
      });
    } catch (e) {
      Sentry.captureException(e);
      throw e;
    }
  };

  // The browser must set the multipart boundary itself.
  public httpPostForm = async (
    url: string,
    options = {},
    formData: FormData,
    bearerToken: string | null = null
  ) => {
    try {
      return fetch(this._buildAPIUrl(url), {
        ...options,
        method: 'POST',
        headers: {
          ...(options['headers'] || {}),
          ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {})
        },
        body: formData
      });
    } catch (e) {
      Sentry.captureException(e);
      throw e;
    }
  };

  public httpPut = async (
    url: string,
    options = {},
    body?: any,
    bearerToken: string | null = null
  ) => {
    try {
      return fetch(this._buildAPIUrl(url), {
        ...options,
        method: 'PUT',
        headers: {
          ...(options['headers'] || {}),
          'Content-Type': 'application/json',
          ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {})
        },
        body: body ? JSON.stringify(body) : null
      });
    } catch (e) {
      Sentry.captureException(e);
      throw e;
    }
  };

  public httpPatch = async (
    url: string,
    options = {},
    body?: any,
    bearerToken: string | null = null
  ) => {
    try {
      return fetch(this._buildAPIUrl(url), {
        ...options,
        method: 'PATCH',
        headers: {
          ...(options['headers'] || {}),
          'Content-Type': 'application/json',
          ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {})
        },
        body: body ? JSON.stringify(body) : null
      });
    } catch (e) {
      Sentry.captureException(e);
      throw e;
    }
  };

  public httpDelete = async (url: string, options = {}, bearerToken: string | null = null) => {
    try {
      return fetch(this._buildAPIUrl(url), {
        ...options,
        method: 'DELETE',
        headers: {
          ...(options['headers'] || {}),
          ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {})
        }
      });
    } catch (e) {
      Sentry.captureException(e);
      throw e;
    }
  };

  private _buildAPIUrl = (url: string): string => {
    return `${this.apiUrl}${url}`;
  };

  private _buildQueryString(params: Record<string, any>): string {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((item) => {
          if (item !== undefined && item !== null) {
            searchParams.append(key, item.toString());
          }
        });
      } else if (value !== undefined && value !== null) {
        searchParams.set(key, value.toString());
      }
    });

    const queryString = searchParams.toString();
    return queryString ? `?${queryString}` : '';
  }
}

export { BaseHttpService };
